import { useEffect, useMemo, useState } from 'react';
import RegisterFormFields from './RegisterFormFields';
import { updateFaceUserProfile } from '../utils/faceIdentityApi';
import {
  buildWelcomeProfileUpdatePayload,
  draftFromWelcomeProfile,
  preferenceRowsFromRegisterForm,
  validateWelcomePreferencesDraft,
} from '../utils/registerFormHelpers';
import './WelcomePreferencesCard.css';

const IconHeart = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M12 20s-7-4.4-7-9.2A3.8 3.8 0 0 1 12 8a3.8 3.8 0 0 1 7 2.8C19 15.6 12 20 12 20z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

const IconEdit = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M4 20h4l10.5-10.5a1.8 1.8 0 0 0-2.5-2.5L5.5 17.5 4 20z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path d="M13.5 6.5l2.5 2.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

/**
 * Preferences card driven by workspace register-form customization.
 * Supports view + edit, saving to the same face user profile.
 */
export default function WelcomePreferencesCard({
  registerForm,
  preferences,
  user,
  membership,
  faceUserId,
  onSaved,
  readOnly = false,
  enableNtliveMobileLookup = false,
  heading = 'Preferences',
  onSaveProfile,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() =>
    draftFromWelcomeProfile(preferences, registerForm, user, membership),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const prefEntries = useMemo(
    () => preferenceRowsFromRegisterForm(preferences, registerForm, user, membership),
    [preferences, registerForm, user, membership],
  );

  useEffect(() => {
    if (editing) return;
    setDraft(draftFromWelcomeProfile(preferences, registerForm, user, membership));
  }, [preferences, registerForm, user, membership, editing]);

  const startEdit = () => {
    setError('');
    setDraft(draftFromWelcomeProfile(preferences, registerForm, user, membership));
    setEditing(true);
  };

  const cancelEdit = () => {
    setError('');
    setDraft(draftFromWelcomeProfile(preferences, registerForm, user, membership));
    setEditing(false);
  };

  const saveEdit = async () => {
    if (!faceUserId || saving) return;
    const validationError = validateWelcomePreferencesDraft(draft, registerForm);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = buildWelcomeProfileUpdatePayload(draft, registerForm);
      const data = onSaveProfile
        ? await onSaveProfile(faceUserId, payload)
        : await updateFaceUserProfile(faceUserId, payload);
      onSaved?.(data);
      setEditing(false);
    } catch (err) {
      setError(err?.message || 'Could not save preferences');
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className={`fi-welcome__tile fi-welcome-prefs${editing ? ' is-editing' : ''}`}>
      <div className="fi-welcome__tile-head fi-welcome-prefs__head">
        <span className="fi-welcome__tile-icon">
          <IconHeart />
        </span>
        <h3>{heading}</h3>
        {!editing && !readOnly ? (
          <button
            type="button"
            className="fi-welcome-prefs__edit-btn"
            onClick={startEdit}
            disabled={!faceUserId}
            aria-label={`Edit ${heading}`}
          >
            <IconEdit />
            Edit
          </button>
        ) : null}
      </div>

      {editing ? (
        <div className="fi-welcome-prefs__editor">
          <RegisterFormFields
            registerForm={registerForm}
            values={draft}
            setValues={setDraft}
            enableNtliveMobileLookup={enableNtliveMobileLookup}
          />
          {error ? <p className="fi-welcome-prefs__error">{error}</p> : null}
          <div className="fi-welcome-prefs__actions">
            <button
              type="button"
              className="fi-welcome-prefs__btn fi-welcome-prefs__btn--ghost"
              onClick={cancelEdit}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="fi-welcome-prefs__btn fi-welcome-prefs__btn--primary"
              onClick={saveEdit}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      ) : prefEntries.length ? (
        prefEntries.map((row) => (
          <div key={row.key} className="fi-welcome__kv">
            <span className="fi-welcome__kv-key">{row.label}</span>
            <span className="fi-welcome__kv-val">{row.value}</span>
          </div>
        ))
      ) : (
        <p>None recorded</p>
      )}
    </article>
  );
}
